/**
 * ─── HRIS Complete: Allowances, Cash Advance, Bonus, Income Types, Banks, Penalties ──
 * Cloned from PayrollProSolutions VB6 forms:
 *   frmAllowancesTable, frmAdvancesTable, frmBonusSetup, frmBank,
 *   frmIncomeTable, frmLatePenaltyTable, frmEmployeeAllowanceAddItem
 */

import { useEffect, useState, useCallback } from 'react'
import { Card, CardHeader, Badge, Button, Input, Modal } from '@repo/ui-core'
import { employeeRepo } from '../../lib/db'
import {
  Plus, DollarSign, Gift, CreditCard, Building2, AlertTriangle, TrendingUp, Users, Percent, Clock,
} from 'lucide-react'

// ═══════════════════════════════════════════════════════════════
// Master Config Page — All Configuration Tables
// ═══════════════════════════════════════════════════════════════

const TABS = ['Allowances', 'Cash Advance', 'Bonus', 'Income Types', 'Banks', 'Penalties', 'Cost Centers', 'Employee Ranks', 'Employee Classes']

interface ConfigRecord {
  id: string; type: string; name: string; value: number; notes: string;
  employeeId?: string; employeeName?: string; date?: string; status?: string;
}

export function HrisConfigPage() {
  const [activeTab, setActiveTab] = useState('Allowances')
  const [records, setRecords] = useState<ConfigRecord[]>([])
  const [employees, setEmployees] = useState<any[]>([])
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState({ name: '', value: 0, notes: '', employeeId: '', date: '', type: '' })

  useEffect(() => {
    ;(async () => {
      try { const r = await employeeRepo.findMany({ page: 1, pageSize: 500 }); setEmployees('items' in r ? r.items : []) } catch { }
    })()
  }, [])

  useEffect(() => {
    const key = 'hris_config_' + activeTab.toLowerCase().replace(' ', '_')
    try { setRecords(JSON.parse(localStorage.getItem(key) || '[]')) } catch { setRecords([]) }
    setForm({ name: '', value: 0, notes: '', employeeId: '', date: '', type: '' })
  }, [activeTab])

  function save() {
    const emp = employees.find((e: any) => e.id === form.employeeId)
    const entry: ConfigRecord = {
      id: Date.now().toString(), type: activeTab,
      name: form.name || activeTab, value: Number(form.value),
      notes: form.notes,
      employeeId: form.employeeId || undefined,
      employeeName: emp?.fullName ?? emp?.name ?? '',
      date: form.date || new Date().toISOString().slice(0, 10),
      status: 'active',
    }
    const key = 'hris_config_' + activeTab.toLowerCase().replace(' ', '_')
    const existing = JSON.parse(localStorage.getItem(key) || '[]')
    const updated = [entry, ...existing]
    localStorage.setItem(key, JSON.stringify(updated))
    setRecords(updated)
    setShowModal(false)
  }

  function remove(id: string) {
    const key = 'hris_config_' + activeTab.toLowerCase().replace(' ', '_')
    const updated = records.filter(r => r.id !== id)
    localStorage.setItem(key, JSON.stringify(updated))
    setRecords(updated)
  }

  const totalValue = records.reduce((s, r) => s + (r.value ?? 0), 0)

  const tabConfigs: Record<string, { icon: any; color: string; fields: string[]; label: string; description: string }> = {
    'Allowances': { icon: Gift, color: 'green', fields: ['name','value','notes'], label: 'Allowance', description: 'Configure allowance types: rice subsidy, uniform, medical, laundry, transportation' },
    'Cash Advance': { icon: CreditCard, color: 'amber', fields: ['employeeId','name','value','date','notes'], label: 'Cash Advance', description: 'Track employee cash advances (CA) with amount and date' },
    'Bonus': { icon: Gift, color: 'purple', fields: ['employeeId','name','value','date','notes'], label: 'Bonus', description: 'Process employee bonuses: performance, 13th month, Christmas, incentive' },
    'Income Types': { icon: DollarSign, color: 'blue', fields: ['name','value','notes'], label: 'Income Type', description: 'Define income categories: basic pay, overtime, holiday, commission, honorarium' },
    'Banks': { icon: Building2, color: 'indigo', fields: ['name','value','notes'], label: 'Bank', description: 'Configure bank accounts for payroll disbursement: bank name, account number' },
    'Penalties': { icon: AlertTriangle, color: 'red', fields: ['name','value','notes'], label: 'Penalty Rule', description: 'Set late/tardiness penalty rates per minute or per incident' },
    'Cost Centers': { icon: Building2, color: 'teal', fields: ['name','value','notes'], label: 'Cost Center', description: 'Define cost centers for payroll allocation: Admin, Operations, Sales, etc.' },
    'Employee Ranks': { icon: TrendingUp, color: 'indigo', fields: ['name','value','notes'], label: 'Employee Rank', description: 'Define employee ranks: Junior, Senior, Lead, Manager, Director, VP' },
    'Employee Classes': { icon: Users, color: 'blue', fields: ['name','value','notes'], label: 'Employee Class', description: 'Define employee classes: Probationary, Regular, Contractual, Project-Based' },
  }

  const cfg = tabConfigs[activeTab] ?? tabConfigs['Allowances']!
  const Icon = cfg.icon

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Payroll Configuration</h2>
        <p className="text-sm text-gray-500 mt-1">Master tables cloned from PayrollProSolutions — allowances, advances, bonus, income types, banks, penalties</p>
      </div>

      {/* Tabs */}
      <div className="mb-6 flex flex-wrap gap-2">
        {TABS.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
            {tab}
          </button>
        ))}
      </div>

      {/* Stats */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className={`rounded-xl border border-${cfg.color}-200 bg-${cfg.color}-50 p-4`}>
          <p className={`text-xs text-${cfg.color}-600`}>Total {activeTab}</p>
          <p className={`mt-1 text-xl font-bold text-${cfg.color}-700`}>{records.length}</p>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-4">
          <p className="text-xs text-blue-600">Total Value</p>
          <p className="mt-1 text-xl font-bold text-blue-700">₱{totalValue.toLocaleString()}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Employees</p>
          <p className="mt-1 text-xl font-bold text-gray-900">{employees.length}</p>
        </div>
      </div>

      {/* Records Table */}
      <Card>
        <CardHeader
          title={`${activeTab} Configuration`}
          description={cfg.description}
          action={<Button onClick={() => { setForm({ name: '', value: 0, notes: '', employeeId: '', date: '', type: activeTab }); setShowModal(true) }} icon={<Plus className="h-4 w-4" />}>Add {cfg.label}</Button>}
        />
        <div className="overflow-x-auto"><table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50"><tr>
            {activeTab === 'Cash Advance' || activeTab === 'Bonus' ? (
              ['Employee','Name','Amount','Date','Notes',''].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)
            ) : (
              ['Name','Value/Amount','Notes',''].map(h=><th key={h} className="px-3 py-2.5 text-left text-xs font-medium uppercase text-gray-500">{h}</th>)
            )}
          </tr></thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {records.length === 0 ? (
              <tr><td colSpan={6} className="py-8 text-center text-gray-400">No {activeTab.toLowerCase()} configured yet. Add one to get started.</td></tr>
            ) : records.map(r => (
              <tr key={r.id} className="hover:bg-gray-50">
                {(activeTab === 'Cash Advance' || activeTab === 'Bonus') && <td className="px-3 py-2.5 text-xs font-medium">{r.employeeName || r.employeeId?.slice(0,8) || '—'}</td>}
                <td className="px-3 py-2.5 font-medium text-xs">{r.name}</td>
                <td className="px-3 py-2.5 text-right font-bold">₱{(r.value ?? 0).toLocaleString()}</td>
                {(activeTab === 'Cash Advance' || activeTab === 'Bonus') && <td className="px-3 py-2.5 text-xs text-gray-500">{r.date}</td>}
                <td className="px-3 py-2.5 text-xs text-gray-500 max-w-[200px] truncate">{r.notes || '—'}</td>
                <td className="px-3 py-2.5"><button onClick={() => remove(r.id)} className="text-xs text-red-500 hover:underline">Remove</button></td>
              </tr>
            ))}
          </tbody>
        </table></div>
      </Card>

      {/* Add Modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title={`Add ${cfg.label}`} size="md"
        footer={<div className="flex gap-2"><Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button><Button onClick={save} icon={<Icon className="h-4 w-4"/>}>Save</Button></div>}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          {(activeTab === 'Cash Advance' || activeTab === 'Bonus') && (
            <div className="sm:col-span-2"><label className="block text-sm font-medium text-gray-700 mb-1">Employee</label>
              <select value={form.employeeId} onChange={e => setForm({...form, employeeId: e.target.value})} className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                <option value="">Select employee...</option>
                {employees.map((e: any) => <option key={e.id} value={e.id}>{e.fullName ?? e.name}</option>)}
              </select></div>
          )}
          <div className={activeTab === 'Cash Advance' || activeTab === 'Bonus' ? '' : 'sm:col-span-2'}>
            <Input label="Name / Description" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder={activeTab === 'Allowances' ? 'Rice Subsidy' : activeTab === 'Banks' ? 'BDO' : activeTab === 'Income Types' ? 'Basic Pay' : activeTab === 'Penalties' ? 'Late per minute' : ''} required />
          </div>
          <Input label={activeTab === 'Banks' ? 'Account Number' : 'Amount / Value (₱)'} type="number" value={String(form.value)} onChange={e => setForm({...form, value: Number(e.target.value)})} />
          {(activeTab === 'Cash Advance' || activeTab === 'Bonus') && (
            <Input label="Date" type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
          )}
          <div className="sm:col-span-2">
            <Input label="Notes" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} placeholder="Optional notes..." />
          </div>
        </div>
      </Modal>
    </div>
  )
}
